import { useEffect, useRef, useState } from "react";
import { UserCheck, Layers, Users, LifeBuoy } from "lucide-react";
import { getContent } from "@/lib/api";

const FALLBACK = { years: "10+", trips: "100+", travellers: "10,000+", coverage: "Pan India" };

function CountUp({ value }) {
    const ref = useRef(null);
    const [display, setDisplay] = useState(value);
    useEffect(() => {
        const match = value.match(/^([\d,]+)(.*)$/);
        if (!match) { setDisplay(value); return; }
        const target = parseInt(match[1].replace(/,/g, ""), 10);
        const suffix = match[2];
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            obs.disconnect();
            const start = performance.now();
            const dur = 1200;
            const tick = (now) => {
                const p = Math.min((now - start) / dur, 1);
                const eased = 1 - Math.pow(1 - p, 3);
                setDisplay(Math.round(target * eased).toLocaleString("en-IN") + suffix);
            };
            requestAnimationFrame(function step(now) { tick(now); if (now - start < dur) requestAnimationFrame(step); });
        }, { threshold: 0.4 });
        obs.observe(el);
        return () => obs.disconnect();
    }, [value]);
    return <span ref={ref}>{display}</span>;
}

const REASONS = [
    { title: "One Point of Contact", desc: "No dealing with multiple vendors.", icon: UserCheck },
    { title: "End-to-End Management", desc: "Travel, stay, food, activities and execution handled together.", icon: Layers },
    { title: "Group Travel Experts", desc: "We understand the unique requirements of schools, colleges and corporates.", icon: Users },
    { title: "Real On-Ground Support", desc: "Our team doesn't disappear after booking. We execute the experience with you.", icon: LifeBuoy },
];

export function WhyUs() {
    const [stats, setStats] = useState(FALLBACK);
    useEffect(() => {
        getContent().then((c) => c?.stats && setStats({ ...FALLBACK, ...c.stats })).catch(() => {});
    }, []);

    const METRICS = [
        { value: stats.years, label: "Years of Execution" },
        { value: stats.trips, label: "Trips & Experiences" },
        { value: stats.travellers, label: "Travellers Managed" },
        { value: stats.coverage, label: "Trip Execution" },
    ];

    return (
        <section id="why" className="py-20 bg-navy text-white" data-testid="why-us-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-gold -rotate-1 inline-block">kyunki zindagi ek safar hai!</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tight">Why Travel With Us?</h2>

                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 pb-12 border-b border-white/10" data-testid="stats-grid">
                    {METRICS.map((m) => (
                        <div key={m.label} className="min-w-0">
                            <div className={`font-display font-black text-gold break-words [overflow-wrap:anywhere] ${String(m.value).length > 8 ? "text-2xl lg:text-4xl leading-tight" : "text-4xl lg:text-6xl"}`} data-testid={`stat-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <CountUp value={m.value} />
                            </div>
                            <div className="mt-2 text-xs uppercase tracking-wider text-white/50 font-bold">{m.label}</div>
                        </div>
                    ))}
                </div>

                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {REASONS.map((r) => (
                        <div key={r.title} className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid={`reason-${r.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <r.icon className="h-6 w-6 text-saffron-light" />
                            <h3 className="mt-4 font-display font-black text-base">{r.title}</h3>
                            <p className="mt-2 text-xs text-white/60 leading-relaxed">{r.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

const STEPS = [
    { num: "01", title: "Tell Us Your Plan", desc: "Share your destination, dates, group size and requirements." },
    { num: "02", title: "We Build Your Trip", desc: "Our travel experts create the itinerary and package." },
    { num: "03", title: "You Approve", desc: "Review, customise and confirm your trip." },
    { num: "04", title: "GhumneChaley!", desc: "We take care of execution while you enjoy the journey." },
];

export function HowItWorks() {
    return (
        <section id="how" className="py-20 border-b border-stone-300/70" data-testid="how-it-works-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">simple as that</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">Your Trip. Four Simple Steps.</h2>

                <div className="mt-14 relative">
                    <div className="hidden md:block absolute top-7 left-[12%] right-[12%] border-t-2 border-dashed border-stone-300" />
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
                        {STEPS.map((s, i) => (
                            <div key={s.num} className="relative text-center md:text-left" data-testid={`step-${s.num}`}>
                                <div className={`mx-auto md:mx-0 h-14 w-14 rounded-full flex items-center justify-center font-display font-black text-lg border-2 ${i === 3 ? "bg-saffron border-saffron text-white" : "bg-cream border-navy text-navy"}`}>
                                    {s.num}
                                </div>
                                <h3 className={`mt-5 font-display font-black text-lg ${i === 3 ? "text-saffron" : "text-navy"}`}>{s.title}</h3>
                                <p className="mt-2 text-sm text-stone-600 leading-relaxed max-w-xs mx-auto md:mx-0">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
